import { useState } from 'react';
import { Modal, Stack, Select, Textarea, Button, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useOverrideModeration } from '@/hooks/useModeration';
import type { ModerationResult, ModerationDecision } from '@/types/moderation';

interface Props {
  item: ModerationResult | null;
  onClose: () => void;
}

export function ModerationOverrideDialog({ item, onClose }: Props) {
  const [decision, setDecision] = useState<ModerationDecision>('ALLOW');
  const [reason, setReason] = useState('');
  const override = useOverrideModeration();

  const handleSubmit = () => {
    if (!item) return;
    override.mutate(
      { offerId: item.offerId, newDecision: decision, reason },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Moderation aktualisiert',
            message: `Offer #${item.offerId} wurde auf ${decision} gesetzt`,
            color: 'green',
          });
          setReason('');
          onClose();
        },
        onError: () => {
          notifications.show({
            title: 'Fehler',
            message: 'Override konnte nicht gespeichert werden',
            color: 'red',
          });
        },
      },
    );
  };

  return (
    <Modal
      opened={!!item}
      onClose={onClose}
      title={`Moderation Override — Offer #${item?.offerId}`}
      size="md"
    >
      <Stack>
        <Text size="sm" c="dimmed">
          Aktuell: <strong>{item?.decision}</strong> ({item?.reason})
        </Text>

        <Select
          label="Neue Entscheidung"
          data={[
            { value: 'ALLOW', label: 'Freigeben (ALLOW)' },
            { value: 'REVIEW', label: 'Zur Prüfung (REVIEW)' },
            { value: 'HOLD', label: 'Blockieren (HOLD)' },
          ]}
          value={decision}
          onChange={(v) => setDecision((v as ModerationDecision) || 'ALLOW')}
        />

        <Textarea
          label="Begründung"
          placeholder="Warum wird diese Entscheidung getroffen?"
          minRows={3}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          required
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} loading={override.isPending} disabled={!reason.trim()}>
            Speichern
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
